'use client';

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import {
  approveBusinessLandingOverride,
  clearBusinessLandingOverride,
  createBusinessMonthlyTarget,
  downloadBusinessMonthlyTargetTemplate,
  getBusinessGovernancePolicies,
  getBusinessMetricDefinitions,
  getBusinessMonthlyTarget,
  getBusinessMonthlyTargets,
  getBusinessMonthlyTargetDimensionOptions,
  getBusinessReportCenters,
  importBusinessMonthlyTargets,
  rejectBusinessLandingOverride,
  requestBusinessLandingOverride,
  updateBusinessMonthlyTarget,
  type BusinessGovernancePolicy,
  type BusinessMetricDefinition,
  type BusinessMonthlyTarget,
  type BusinessMonthlyTargetInput,
  type BusinessReportCenter,
} from '../lib/api/business-reports';
import { downloadBlob } from '../lib/api/dashboard';
import { AdminAlertModal, Loader } from './common';
import {
  cleanSpaces,
  dateOnly,
  dateTime,
  errorMessage,
  exportCsv,
  FinanceField,
  FinanceModal,
  FinanceStatus,
  formatNumber,
  label,
} from './BusinessReportFinanceUi';

type TargetForm = {
  targetMonth: string;
  centerCode: string;
  metricKey: string;
  businessDomainKey: string;
  accountableTeamKey: string;
  targetValue: string;
  targetSource: string;
  targetOwner: string;
  governanceConfirmed: boolean;
};

const EMPTY_FORM: TargetForm = {
  targetMonth: '',
  centerCode: '',
  metricKey: '',
  businessDomainKey: '',
  accountableTeamKey: '',
  targetValue: '',
  targetSource: '',
  targetOwner: '',
  governanceConfirmed: false,
};

type OverrideDialog = {
  kind: 'request' | 'reject' | 'clear';
  target: BusinessMonthlyTarget;
  value: string;
  reason: string;
  confirmed: boolean;
};

type TargetDimensionOption = {
  key: string;
  displayName: string;
};

type TargetDimensions = {
  businessDomains: TargetDimensionOption[];
  accountableTeams: TargetDimensionOption[];
};

const EMPTY_DIMENSIONS: TargetDimensions = {
  businessDomains: [],
  accountableTeams: [],
};

export function BusinessMonthlyTargetsTab({
  active,
  token,
  userName,
  centerAccess,
  allCenterAccess,
  canCreate,
  canUpdate,
  canExport,
  canRequestOverride,
  canApproveOverride,
  canClearOverride,
  onTopBarActionsChange,
}: {
  active: boolean;
  token?: string;
  userName: string;
  centerAccess: string[];
  allCenterAccess: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canExport: boolean;
  canRequestOverride: boolean;
  canApproveOverride: boolean;
  canClearOverride: boolean;
  onTopBarActionsChange?: (actions: ReactNode) => void;
}) {
  const [targets, setTargets] = useState<BusinessMonthlyTarget[]>([]);
  const [centers, setCenters] = useState<BusinessReportCenter[]>([]);
  const [metricDefinitions, setMetricDefinitions] = useState<BusinessMetricDefinition[]>([]);
  const [governancePolicies, setGovernancePolicies] = useState<BusinessGovernancePolicy[]>([]);
  const [centerCode, setCenterCode] = useState('ALL');
  const [metricKey, setMetricKey] = useState('');
  const [businessDomainKey, setBusinessDomainKey] = useState('ALL');
  const [accountableTeamKey, setAccountableTeamKey] = useState('ALL');
  const [fromMonth, setFromMonth] = useState('');
  const [toMonth, setToMonth] = useState('');
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<BusinessMonthlyTarget | null | undefined>(undefined);
  const [form, setForm] = useState<TargetForm>(EMPTY_FORM);
  const [detail, setDetail] = useState<BusinessMonthlyTarget | null>(null);
  const [overrideDialog, setOverrideDialog] = useState<OverrideDialog | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importBusy, setImportBusy] = useState(false);
  const [templateBusy, setTemplateBusy] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info'; title: string; message: string } | null>(null);

  const scopedCenters = useMemo(() => centers.filter((center) => (
    center.isActive
    && center.approvalStatus === 'APPROVED'
    && center.accessCenterName.toLowerCase() !== 'management'
    && (allCenterAccess || centerAccess.includes(center.accessCenterName))
  )), [allCenterAccess, centerAccess, centers]);
  const activeMetrics = useMemo(
    () => metricDefinitions.filter((metric) => (
      metric.isActive
      && metric.approvalStatus === 'APPROVED'
      && isCurrentlyEffective(metric.effectiveFrom, metric.effectiveTo)
    )),
    [metricDefinitions],
  );
  const br009Active = governancePolicies.some((policy) => (
    policy.policyKey === 'BR-0-09'
    && policy.isActive
    && policy.approvalStatus === 'APPROVED'
    && isCurrentlyEffective(policy.effectiveFrom, policy.effectiveTo)
  ));
  const configuredTargetDimensionsPolicy = governancePolicies.find((policy) => (
    policy.policyKey === 'BR-TARGET-DIMENSIONS'
  ));
  const targetDimensionsPolicy = governancePolicies.find((policy) => (
    policy.policyKey === 'BR-TARGET-DIMENSIONS'
    && policy.isActive
    && policy.approvalStatus === 'APPROVED'
    && isCurrentlyEffective(policy.effectiveFrom, policy.effectiveTo)
  ));
  const [targetDimensions, setTargetDimensions] = useState<TargetDimensions>(EMPTY_DIMENSIONS);
  const targetDimensionsActive = Boolean(
    targetDimensionsPolicy
    && targetDimensions.businessDomains.length
    && targetDimensions.accountableTeams.length,
  );
  const targetMetricOptions = useMemo(
    () => activeMetrics.length
      ? activeMetrics
      : metricDefinitions.filter((metric) => isCurrentlyEffective(metric.effectiveFrom, metric.effectiveTo)),
    [activeMetrics, metricDefinitions],
  );
  const targetCreationBlockers = useMemo(() => [
    ...(!scopedCenters.length ? ['No permitted active centre is available.'] : []),
    ...(!activeMetrics.length ? ['Approve and activate at least one Metric Definition in Target Governance.'] : []),
    ...(!targetDimensionsActive ? ['Approve and activate Monthly Target Domains & Teams in Target Governance.'] : []),
  ], [activeMetrics.length, scopedCenters.length, targetDimensionsActive]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [targetRows, centerRows, metricRows, policyRows, permittedDimensions] = await Promise.all([
        getBusinessMonthlyTargets(token, {
          centerCode,
          metricKey,
          businessDomainKey,
          accountableTeamKey,
          targetMonthFrom: fromMonth,
          targetMonthTo: toMonth,
          take: 500,
        }),
        getBusinessReportCenters(token, { status: 'ACTIVE' }),
        getBusinessMetricDefinitions(token),
        getBusinessGovernancePolicies(token),
        getBusinessMonthlyTargetDimensionOptions(token),
      ]);
      setTargets(targetRows);
      setCenters(centerRows);
      setMetricDefinitions(metricRows);
      setGovernancePolicies(policyRows);
      setTargetDimensions(permittedDimensions);
    } catch (error) {
      setAlert({ type: 'error', title: 'Could not load Monthly Targets', message: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  }, [accountableTeamKey, businessDomainKey, centerCode, fromMonth, metricKey, toMonth, token]);

  useEffect(() => {
    if (active) void load();
  }, [active, load]);

  useEffect(() => {
    if (centerCode !== 'ALL' && !scopedCenters.some((center) => center.centerCode === centerCode)) {
      setCenterCode('ALL');
    }
  }, [centerCode, scopedCenters]);

  const pendingCount = targets.filter((target) => target.pendingLandingOverrideValue !== null).length;
  const approvedOverrideCount = targets.filter((target) => target.landingOverrideValue !== null).length;

  const openCreate = useCallback(() => {
    const firstMetric = targetMetricOptions[0];
    setForm({
      ...EMPTY_FORM,
      targetMonth: new Date().toISOString().slice(0, 7),
      centerCode: scopedCenters[0]?.centerCode || '',
      metricKey: firstMetric?.metricKey || '',
      businessDomainKey: targetDimensions.businessDomains[0]?.key || '',
      accountableTeamKey: targetDimensions.accountableTeams[0]?.key || '',
      targetSource: firstMetric?.permittedTargetSource || '',
      targetOwner: firstMetric?.targetOwner || '',
    });
    setEditing(null);
  }, [scopedCenters, targetDimensions, targetMetricOptions]);

  const openEdit = (target: BusinessMonthlyTarget) => {
    setForm({
      targetMonth: target.targetMonth.slice(0, 7),
      centerCode: target.center.centerCode,
      metricKey: target.metricKey,
      businessDomainKey: target.businessDomainKey,
      accountableTeamKey: target.accountableTeamKey,
      targetValue: String(target.targetValue),
      targetSource: target.targetSource,
      targetOwner: target.targetOwner,
      governanceConfirmed: false,
    });
    setEditing(target);
  };

  const save = async () => {
    setLoading(true);
    try {
      const input = targetInput(form);
      if (editing) await updateBusinessMonthlyTarget(editing.id, input, token);
      else await createBusinessMonthlyTarget(input, token);
      setEditing(undefined);
      await load();
      setAlert({ type: 'success', title: editing ? 'Monthly Target updated' : 'Monthly Target created', message: 'The governed target change and audit history were recorded.' });
    } catch (error) {
      setAlert({ type: 'error', title: 'Could not save Monthly Target', message: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  const view = async (target: BusinessMonthlyTarget) => {
    setLoading(true);
    try {
      setDetail(await getBusinessMonthlyTarget(target.id, token));
    } catch (error) {
      setAlert({ type: 'error', title: 'Could not load target history', message: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  const approve = async (target: BusinessMonthlyTarget) => {
    if (!window.confirm(`Approve the pending landing override ${formatNumber(target.pendingLandingOverrideValue || 0)} for ${target.center.displayName} / ${target.metricKey}?`)) return;
    await runAction('Landing override approved', () => approveBusinessLandingOverride(target.id, token));
  };

  const saveOverrideDialog = async () => {
    if (!overrideDialog) return;
    const reason = cleanSpaces(overrideDialog.reason);
    if (!reason) {
      setAlert({ type: 'error', title: 'Reason required', message: 'Enter the governed reason or evidence reference.' });
      return;
    }
    if (overrideDialog.kind === 'request') {
      if (!overrideDialog.confirmed) {
        setAlert({ type: 'error', title: 'Explicit confirmation required', message: 'Confirm that the BR-0-09 approval and evidence requirements have been satisfied.' });
        return;
      }
      if (!/^\d{1,16}(?:\.\d{1,2})?$/.test(overrideDialog.value.trim())) {
        setAlert({ type: 'error', title: 'Invalid landing value', message: 'Landing override must be non-negative with at most two decimal places.' });
        return;
      }
      await runAction('Landing override requested', () => requestBusinessLandingOverride(overrideDialog.target.id, { value: overrideDialog.value.trim(), reason }, token));
    } else if (overrideDialog.kind === 'reject') {
      await runAction('Landing override rejected', () => rejectBusinessLandingOverride(overrideDialog.target.id, reason, token));
    } else {
      await runAction('Landing override cleared', () => clearBusinessLandingOverride(overrideDialog.target.id, reason, token));
    }
    setOverrideDialog(null);
  };

  const runAction = async (successTitle: string, operation: () => Promise<unknown>) => {
    setLoading(true);
    try {
      await operation();
      setDetail(null);
      await load();
      setAlert({ type: 'success', title: successTitle, message: 'The maker-checker action and immutable target history were recorded.' });
    } catch (error) {
      setAlert({ type: 'error', title: 'Landing override action failed', message: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  const download = useCallback(() => exportCsv(
    'business-monthly-targets.csv',
    ['Month', 'Centre Code', 'Centre', 'Business Domain', 'Accountable Team', 'Metric', 'Metric Unit', 'Target', 'Source', 'Owner', 'Pending Landing', 'Pending Maker', 'Approved Landing', 'Approved By'],
    targets.map((target) => [
      target.targetMonth.slice(0, 7),
      target.center.centerCode,
      target.center.displayName,
      dimensionName(target.businessDomainKey, targetDimensions.businessDomains),
      dimensionName(target.accountableTeamKey, targetDimensions.accountableTeams),
      target.metricKey,
      target.metricDefinition?.unit || '',
      target.targetValue,
      target.targetSource,
      target.targetOwner,
      target.pendingLandingOverrideValue,
      target.pendingLandingOverrideEnteredBy,
      target.landingOverrideValue,
      target.landingOverrideApprovedBy,
    ]),
  ), [targetDimensions, targets]);

  const downloadTemplate = useCallback(async () => {
    setTemplateBusy(true);
    try {
      const result = await downloadBusinessMonthlyTargetTemplate(token);
      downloadBlob(result.blob, result.fileName || 'Ferty9_Monthly_Targets_Template.xlsx');
    } catch (error) {
      setAlert({ type: 'error', title: 'Template download failed', message: errorMessage(error) });
    } finally {
      setTemplateBusy(false);
    }
  }, [token]);

  const uploadTargets = async () => {
    if (!importFile) {
      setAlert({ type: 'error', title: 'Select a file', message: 'Choose an Excel (.xlsx) or CSV Monthly Target file.' });
      return;
    }
    setImportBusy(true);
    try {
      const result = await importBusinessMonthlyTargets(importFile, token);
      setImportOpen(false);
      setImportFile(null);
      await load();
      const errorSummary = result.errors.slice(0, 8).map((entry) => `Row ${entry.rowNumber}: ${entry.message}`).join(' ');
      setAlert({
        type: result.failedRows ? 'info' : 'success',
        title: result.failedRows ? 'Monthly Target import completed with exceptions' : 'Monthly Targets imported',
        message: `${result.createdRows} created, ${result.updatedRows} updated, ${result.failedRows} failed from ${result.totalRows} row(s).${errorSummary ? ` ${errorSummary}` : ''}`,
      });
    } catch (error) {
      setAlert({ type: 'error', title: 'Monthly Target upload failed', message: errorMessage(error) });
    } finally {
      setImportBusy(false);
    }
  };

  const topBarActions = useMemo(() => (
    <div className="business-finance-actions target-topbar-actions">
      <button
        className="btn compact-btn target-topbar-action is-template"
        onClick={() => void downloadTemplate()}
        disabled={templateBusy}
      >
        {templateBusy ? 'Preparing...' : 'Download Blank Template'}
      </button>
      {canCreate && canUpdate && (
        <button
          className="btn compact-btn target-topbar-action is-upload"
          onClick={() => { setImportFile(null); setImportOpen(true); }}
        >
          Upload Excel / CSV
        </button>
      )}
      {canExport && (
        <button className="btn compact-btn target-topbar-action is-export" onClick={download} disabled={!targets.length}>
          Export Scoped CSV
        </button>
      )}
      {canCreate && (
        <button
          className="btn compact-btn target-topbar-action is-create"
          aria-label="New Monthly Target"
          onClick={openCreate}
          data-readiness-blocked={targetCreationBlockers.length > 0 ? 'true' : undefined}
          title={targetCreationBlockers.length ? targetCreationBlockers.join(' ') : 'Create a governed Monthly Target'}
        >
          + New Monthly Target
        </button>
      )}
    </div>
  ), [canCreate, canExport, canUpdate, download, downloadTemplate, openCreate, targetCreationBlockers, targets.length, templateBusy]);

  useEffect(() => {
    if (!active || !onTopBarActionsChange) return undefined;
    onTopBarActionsChange(topBarActions);
    return () => onTopBarActionsChange(null);
  }, [active, onTopBarActionsChange, topBarActions]);

  if (!active) return null;

  return (
    <section className="tab-content business-source-page" id="tab-business-monthly-targets">
      {loading && <Loader message="Processing Monthly Targets..." />}
      <div className={`business-finance-policy-banner ${activeMetrics.length && targetDimensionsActive && br009Active ? 'success' : 'warning'}`}><strong>Server-enforced governance</strong><span>{activeMetrics.length ? `${activeMetrics.length} active approved metric definition(s).` : 'Monthly target writes are blocked until a metric definition is approved and activated.'} {targetDimensionsActive ? `Target dimensions policy v${targetDimensionsPolicy?.version} is active with ${targetDimensions.businessDomains.length} domain(s) and ${targetDimensions.accountableTeams.length} team(s).` : 'Target writes are blocked until Monthly Target Domains & Teams is approved and activated.'} {br009Active ? 'BR-0-09 is active.' : 'Landing override requests are blocked until BR-0-09 is approved and activated.'}</span></div>
      <div className="business-finance-kpis monthly-target-kpis"><div><span>Filtered targets</span><strong>{targets.length}</strong></div><div><span>Pending overrides</span><strong>{pendingCount}</strong></div><div><span>Approved overrides</span><strong>{approvedOverrideCount}</strong></div></div>
      <div className="enterprise-card enterprise-table-card monthly-target-workspace-card">
        <div className="business-finance-filters enterprise-filter-grid monthly-target-filter-grid">
          <FinanceField label="Centre"><select value={centerCode} onChange={(event) => setCenterCode(event.target.value)}><option value="ALL">All permitted centres</option>{scopedCenters.map((center) => <option key={center.id} value={center.centerCode}>{center.displayName}</option>)}</select></FinanceField>
          <FinanceField label="Metric Key"><input value={metricKey} onChange={(event) => setMetricKey(event.target.value.toUpperCase())} placeholder="Approved metric key" /></FinanceField>
          <FinanceField label="Business Domain"><select value={businessDomainKey} onChange={(event) => setBusinessDomainKey(event.target.value)}><option value="ALL">All business domains</option>{targetDimensions.businessDomains.map((entry) => <option key={entry.key} value={entry.key}>{entry.displayName}</option>)}</select></FinanceField>
          <FinanceField label="Accountable Team"><select value={accountableTeamKey} onChange={(event) => setAccountableTeamKey(event.target.value)}><option value="ALL">All accountable teams</option>{targetDimensions.accountableTeams.map((entry) => <option key={entry.key} value={entry.key}>{entry.displayName}</option>)}</select></FinanceField>
          <FinanceField label="From Month"><input type="month" value={fromMonth} onChange={(event) => setFromMonth(event.target.value)} /></FinanceField>
          <FinanceField label="To Month"><input type="month" value={toMonth} onChange={(event) => setToMonth(event.target.value)} /></FinanceField>
          <button className="btn target-filter-refresh" onClick={() => void load()}>Refresh</button>
        </div>
        <div className="enterprise-table-wrap"><table className="enterprise-table monthly-target-table"><thead><tr><th>Month / Centre</th><th>Domain / Team</th><th>Metric / Unit</th><th>Target</th><th>Source / Owner</th><th>Landing Override</th><th>Audit State</th><th>Actions</th></tr></thead><tbody>
          {targets.map((target) => {
            const selfPending = target.pendingLandingOverrideEnteredBy === userName;
            return <tr key={target.id}>
              <td><strong>{target.targetMonth.slice(0, 7)}</strong><small className="business-finance-subline">{target.center.displayName} ({target.center.centerCode})</small></td>
              <td><strong>{dimensionName(target.businessDomainKey, targetDimensions.businessDomains)}</strong><small className="business-finance-subline">{dimensionName(target.accountableTeamKey, targetDimensions.accountableTeams)}</small></td>
              <td>{target.metricDefinition?.displayName || target.metricKey}<small className="business-finance-subline">{target.metricKey} · {target.metricDefinition?.unit || 'Unit unavailable'} · v{target.metricDefinitionVersion}</small></td>
              <td><strong>{formatNumber(target.targetValue)}</strong></td>
              <td>{target.targetSource}<small className="business-finance-subline">{target.targetOwner}</small></td>
              <td>{target.pendingLandingOverrideValue !== null ? <><FinanceStatus value="PENDING" /><small className="business-finance-subline">{formatNumber(target.pendingLandingOverrideValue)} by {target.pendingLandingOverrideEnteredBy}</small></> : target.landingOverrideValue !== null ? <><FinanceStatus value="APPROVED" /><small className="business-finance-subline">{formatNumber(target.landingOverrideValue)} by {target.landingOverrideApprovedBy}</small></> : '—'}</td>
              <td>{target.pendingLandingOverrideValue !== null ? 'Maker-checker pending' : target.landingOverrideValue !== null ? 'Approved projection' : 'Base target only'}<small className="business-finance-subline">Dimension policy v{target.targetDimensionPolicyVersion || 'legacy'}</small></td>
              <td><div className="business-finance-row-actions monthly-target-actions"><button className="btn btn-sm target-action is-history" onClick={() => void view(target)}>History</button>{canUpdate && target.pendingLandingOverrideValue === null && <button className="btn btn-sm target-action is-edit" onClick={() => openEdit(target)}>Edit</button>}{canRequestOverride && target.pendingLandingOverrideValue === null && <button className="btn btn-sm target-action is-request" disabled={!br009Active} title={!br009Active ? 'BR-0-09 is not active' : undefined} onClick={() => setOverrideDialog({ kind: 'request', target, value: '', reason: '', confirmed: false })}>Request Override</button>}{canApproveOverride && target.pendingLandingOverrideValue !== null && <><button className="btn btn-sm target-action is-approve" disabled={selfPending} title={selfPending ? 'Maker cannot approve their own override' : undefined} onClick={() => void approve(target)}>Approve</button><button className="btn btn-sm target-action is-reject" disabled={selfPending} onClick={() => setOverrideDialog({ kind: 'reject', target, value: '', reason: '', confirmed: true })}>Reject</button></>}{canClearOverride && target.landingOverrideValue !== null && target.pendingLandingOverrideValue === null && <button className="btn btn-sm target-action is-deactivate" onClick={() => setOverrideDialog({ kind: 'clear', target, value: '', reason: '', confirmed: true })}>Clear Override</button>}</div></td>
            </tr>;
          })}
          {!targets.length && <tr><td colSpan={8} className="empty-cell">No Monthly Targets match the selected scoped filters.</td></tr>}
        </tbody></table></div>
      </div>
      {editing !== undefined && <TargetEditor editing={editing} form={form} setForm={setForm} centers={scopedCenters} metrics={targetMetricOptions} dimensions={targetDimensions} blockers={targetCreationBlockers} onClose={() => setEditing(undefined)} onSave={() => void save()} />}
      {detail && <TargetDetail target={detail} onClose={() => setDetail(null)} />}
      {overrideDialog && <OverrideEditor dialog={overrideDialog} setDialog={setOverrideDialog} onClose={() => setOverrideDialog(null)} onSave={() => void saveOverrideDialog()} />}
      {importOpen && <TargetImportModal file={importFile} busy={importBusy} onFile={setImportFile} onClose={() => { if (!importBusy) { setImportOpen(false); setImportFile(null); } }} onUpload={() => void uploadTargets()} />}
      {alert && <AdminAlertModal title={alert.title} message={alert.message} type={alert.type} onClose={() => setAlert(null)} />}
    </section>
  );
}

function TargetImportModal({ file, busy, onFile, onClose, onUpload }: {
  file: File | null;
  busy: boolean;
  onFile: (file: File | null) => void;
  onClose: () => void;
  onUpload: () => void;
}) {
  return <FinanceModal
    title="Upload Monthly Targets"
    subtitle="Create new targets or update existing targets from the governed Excel template or a UTF-8 CSV."
    wide
    onClose={onClose}
    footer={<><span>Maximum 500 data rows and 5 MB. Every row remains centre-, domain- and team-scoped.</span><div><button className="btn btn-outline" onClick={onClose} disabled={busy}>Cancel</button><button className="btn btn-primary" onClick={onUpload} disabled={busy || !file}>{busy ? 'Uploading...' : 'Upload Targets'}</button></div></>}
  >
    <div className="monthly-target-import-panel">
      <div className="monthly-target-import-instructions">
        <strong>Required columns</strong>
        <span>Target Month, Centre Code, Metric Key, Business Domain Key, Accountable Team Key and Target Value.</span>
        <small>Target source, owner and unit are taken from the current approved Metric Definition Master. Formula cells and duplicate target keys are rejected.</small>
      </div>
      <label className={`monthly-target-file-picker ${file ? 'has-file' : ''}`}>
        <input
          type="file"
          accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
          disabled={busy}
          onChange={(event) => onFile(event.target.files?.[0] || null)}
        />
        <span>{file ? 'Selected file' : 'Choose Excel or CSV file'}</span>
        <strong>{file?.name || 'No file selected'}</strong>
        {file && <small>{(file.size / 1024).toFixed(1)} KB</small>}
      </label>
    </div>
  </FinanceModal>;
}

function isCurrentlyEffective(effectiveFrom: string | null, effectiveTo: string | null): boolean {
  const today = new Date().toISOString().slice(0, 10);
  return (!effectiveFrom || effectiveFrom.slice(0, 10) <= today)
    && (!effectiveTo || effectiveTo.slice(0, 10) >= today);
}

function TargetEditor({ editing, form, setForm, centers, metrics, dimensions, blockers, onClose, onSave }: {
  editing: BusinessMonthlyTarget | null;
  form: TargetForm;
  setForm: (updater: (current: TargetForm) => TargetForm) => void;
  centers: BusinessReportCenter[];
  metrics: BusinessMetricDefinition[];
  dimensions: TargetDimensions;
  blockers: string[];
  onClose: () => void;
  onSave: () => void;
}) {
  const selectedMetric = metrics.find((metric) => metric.metricKey === form.metricKey);
  return <FinanceModal title={editing ? 'Edit Monthly Target' : 'New Monthly Target'} subtitle="One row is permitted for each month + centre + metric + business domain + accountable team." wide onClose={onClose} footer={<><span>{blockers.length ? 'Complete the displayed Target Governance prerequisites before saving.' : 'The target will be validated and audited by the server.'}</span><div><button className="btn btn-outline" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={onSave} disabled={blockers.length > 0} title={blockers.length ? blockers.join(' ') : undefined}>Save Monthly Target</button></div></>}>
    {blockers.length > 0 && <div className="business-finance-policy-banner warning business-target-readiness"><strong>Target setup required</strong><span>{blockers.join(' ')}</span></div>}
    <div className="business-source-form-grid">
      <FinanceField label="Target Month"><input type="month" value={form.targetMonth} onChange={(event) => setForm((current) => ({ ...current, targetMonth: event.target.value }))} /></FinanceField>
      <FinanceField label="Centre"><select value={form.centerCode} onChange={(event) => setForm((current) => ({ ...current, centerCode: event.target.value }))}><option value="">Select permitted centre</option>{centers.map((center) => <option key={center.id} value={center.centerCode}>{center.displayName}</option>)}</select></FinanceField>
      <FinanceField label="Business Domain"><select value={form.businessDomainKey} onChange={(event) => setForm((current) => ({ ...current, businessDomainKey: event.target.value, governanceConfirmed: false }))}><option value="">Select business domain</option>{dimensions.businessDomains.map((entry) => <option key={entry.key} value={entry.key}>{entry.displayName} ({entry.key})</option>)}</select></FinanceField>
      <FinanceField label="Accountable Team"><select value={form.accountableTeamKey} onChange={(event) => setForm((current) => ({ ...current, accountableTeamKey: event.target.value, governanceConfirmed: false }))}><option value="">Select accountable team</option>{dimensions.accountableTeams.map((entry) => <option key={entry.key} value={entry.key}>{entry.displayName} ({entry.key})</option>)}</select></FinanceField>
      <FinanceField label="Metric Definition" help="Only active, approved definitions are available."><select value={form.metricKey} onChange={(event) => { const metric = metrics.find((row) => row.metricKey === event.target.value); setForm((current) => ({ ...current, metricKey: event.target.value, targetSource: metric?.permittedTargetSource || '', targetOwner: metric?.targetOwner || '', governanceConfirmed: false })); }}><option value="">Select approved metric</option>{metrics.map((metric) => <option key={metric.id} value={metric.metricKey}>{metric.displayName} ({metric.metricKey}) · {metric.unit}</option>)}</select></FinanceField>
      <FinanceField label="Measurement Unit"><input value={selectedMetric?.unit || ''} readOnly placeholder="Select an approved metric" /></FinanceField>
      <FinanceField label="Target Value"><input inputMode="decimal" value={form.targetValue} onChange={(event) => setForm((current) => ({ ...current, targetValue: event.target.value }))} /></FinanceField>
      <FinanceField label="Target Source"><input value={form.targetSource} readOnly /></FinanceField>
      <FinanceField label="Target Owner"><input value={form.targetOwner} readOnly /></FinanceField>
      <label className="business-finance-confirm full"><input type="checkbox" checked={form.governanceConfirmed} onChange={(event) => setForm((current) => ({ ...current, governanceConfirmed: event.target.checked }))} /><span>I confirm the centre, business domain, accountable team, metric definition, unit, target source and owner match the approved target plan.</span></label>
    </div>
  </FinanceModal>;
}

function OverrideEditor({ dialog, setDialog, onClose, onSave }: {
  dialog: OverrideDialog;
  setDialog: (dialog: OverrideDialog) => void;
  onClose: () => void;
  onSave: () => void;
}) {
  const titles = { request: 'Request Landing Override', reject: 'Reject Landing Override', clear: 'Clear Approved Landing Override' };
  return <FinanceModal title={titles[dialog.kind]} subtitle={`${dialog.target.center.displayName} — ${dialog.target.metricKey} — ${dialog.target.targetMonth.slice(0, 7)}`} onClose={onClose} footer={<><span>Every override action is audited.</span><div><button className="btn btn-outline" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={onSave}>Confirm {label(dialog.kind)}</button></div></>}>
    <div className="business-source-form-grid">
      {dialog.kind === 'request' && <FinanceField label="Projected Landing Value" full><input inputMode="decimal" value={dialog.value} onChange={(event) => setDialog({ ...dialog, value: event.target.value })} /></FinanceField>}
      <FinanceField label={dialog.kind === 'reject' ? 'Rejection Reason' : dialog.kind === 'clear' ? 'Clearing Reason' : 'Override Reason and Evidence'} full><textarea rows={4} value={dialog.reason} onChange={(event) => setDialog({ ...dialog, reason: event.target.value })} /></FinanceField>
      {dialog.kind === 'request' && <label className="business-finance-confirm full"><input type="checkbox" checked={dialog.confirmed} onChange={(event) => setDialog({ ...dialog, confirmed: event.target.checked })} /><span>I explicitly confirm the BR-0-09 policy approval, evidence and maker-checker requirements have been satisfied.</span></label>}
    </div>
  </FinanceModal>;
}

function TargetDetail({ target, onClose }: { target: BusinessMonthlyTarget; onClose: () => void }) {
  return <FinanceModal title="Monthly Target Audit History" subtitle={`${target.center.displayName} — ${target.metricKey}`} wide onClose={onClose} footer={<><span>Pending override values are never treated as approved projections.</span><button className="btn btn-primary" onClick={onClose}>Close</button></>}>
    <div className="business-finance-detail-grid"><div><span>Month</span><strong>{target.targetMonth.slice(0, 7)}</strong></div><div><span>Business Domain</span><strong>{target.businessDomainKey}</strong></div><div><span>Accountable Team</span><strong>{target.accountableTeamKey}</strong></div><div><span>Metric Unit</span><strong>{target.metricDefinition?.unit || '—'}</strong></div><div><span>Base Target</span><strong>{formatNumber(target.targetValue)}</strong></div><div><span>Source</span><strong>{target.targetSource}</strong></div><div><span>Owner</span><strong>{target.targetOwner}</strong></div><div><span>Dimension Approval</span><strong>{target.targetDimensionApprovalReference || 'Legacy target'}</strong></div><div><span>Pending Landing</span><strong>{target.pendingLandingOverrideValue === null ? '—' : formatNumber(target.pendingLandingOverrideValue)}</strong></div><div><span>Approved Landing</span><strong>{target.landingOverrideValue === null ? '—' : formatNumber(target.landingOverrideValue)}</strong></div></div>
    <div className="enterprise-table-wrap"><table className="enterprise-table"><thead><tr><th>Revision</th><th>Action</th><th>Performed By</th><th>Time</th></tr></thead><tbody>{target.history?.map((row) => <tr key={row.revision}><td>{row.revision}</td><td>{label(row.action)}</td><td>{row.performedBy}</td><td>{dateTime(row.performedAt)}</td></tr>)}{!target.history?.length && <tr><td colSpan={4} className="empty-cell">No history revisions returned.</td></tr>}</tbody></table></div>
  </FinanceModal>;
}

function targetInput(form: TargetForm): BusinessMonthlyTargetInput {
  if (!form.governanceConfirmed) throw new Error('Confirm the approved centre, dimensions, metric definition, source and owner before saving.');
  if (!form.targetMonth || !form.centerCode) throw new Error('Target month and centre are required.');
  const businessDomainKey = cleanSpaces(form.businessDomainKey).toUpperCase();
  const accountableTeamKey = cleanSpaces(form.accountableTeamKey).toUpperCase();
  if (!businessDomainKey || !accountableTeamKey) throw new Error('Business domain and accountable team are required.');
  const metricKey = cleanSpaces(form.metricKey).toUpperCase().replace(/[^A-Z0-9]+/g, '_');
  if (metricKey.length < 3) throw new Error('An approved metric key is required.');
  if (!/^\d{1,16}(?:\.\d{1,2})?$/.test(form.targetValue.trim())) throw new Error('Target value must be non-negative with at most two decimal places.');
  const targetSource = cleanSpaces(form.targetSource);
  const targetOwner = cleanSpaces(form.targetOwner);
  if (!targetSource || !targetOwner) throw new Error('Target source and target owner are required.');
  return {
    targetMonth: form.targetMonth,
    centerCode: form.centerCode,
    metricKey,
    businessDomainKey,
    accountableTeamKey,
    targetValue: form.targetValue.trim(),
    targetSource,
    targetOwner,
  };
}

function readTargetDimensions(configuration: Record<string, unknown>): TargetDimensions {
  const readList = (value: unknown): TargetDimensionOption[] => (
    Array.isArray(value)
      ? value.flatMap((entry) => {
          if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return [];
          const row = entry as Record<string, unknown>;
          const key = typeof row.key === 'string' ? row.key.trim().toUpperCase() : '';
          const displayName = typeof row.displayName === 'string' ? cleanSpaces(row.displayName) : '';
          return key && displayName ? [{ key, displayName }] : [];
        })
      : []
  );
  return {
    businessDomains: readList(configuration.businessDomains),
    accountableTeams: readList(configuration.accountableTeams),
  };
}

function dimensionName(key: string, options: TargetDimensionOption[]): string {
  return options.find((entry) => entry.key === key)?.displayName || key;
}
